import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import Navigation from './ContainersBrafab/Navigation';
import FacetedSearch from './ComponentsBrafab/FacetedSearch';
import FacetedSearchCompactContainer from './ContainersBrafab/FacetedSearchCompactContainer';
import DynamicComponent from './Components/DynamicComponent';
import MiniCart from './ComponentsBrafab/MiniCart';
import QuickSearch from './ComponentsBrafab/QuickSearch';
import LanguagePickerNavigationContainer from './ContainersBrafab/LanguagePickerNavigationContainer';
import LanguagePickerFooter from './ComponentsBrafab/LanguagePickerFooter';

let lightBoxImagesInitiated = false;
let pageTimer = null;
window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
    app,
    preloadState,
    composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
    if (!store.asyncReducers[key]) {
        store.asyncReducers[key] = asyncReducer;
        store.replaceReducer(createReducer(store.asyncReducers));
    }
};

window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(element, container, callback);
};

window.onpagehide = () => {

    clearTimeout(pageTimer);

    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach((container) => {
        ReactDOM.unmountComponentAtNode(container);
    });
};

const bootstrapComponents = () => {
    var navbar = document.getElementById('navbar');
    if (navbar) {
        renderReact(
            <Provider store={store}>
                <Navigation />
            </Provider>,
            navbar
        );
    }

    var quickSearch = document.getElementById('quickSearch');
    if (quickSearch) {
        renderReact(
            <Provider store={store}>
                <QuickSearch />
            </Provider>,
            quickSearch
        );
    }

    var miniCart = document.getElementById('miniCart');
    if (miniCart) {
        renderReact(
            <Provider store={store}>
                <MiniCart />
            </Provider>,
            miniCart
        );
    }

    var languagePickerHeader = document.getElementById('js-languagePickerHeader');
    if (languagePickerHeader) {
        renderReact(
            <Provider store={store}>
                <LanguagePickerNavigationContainer isMobile={false} />
            </Provider>,
            languagePickerHeader
        );
    }

    var favouritesEl = document.getElementById('favourites');
    if (favouritesEl) {
        const FavouritesMenu = DynamicComponent({
            loader: () => import('./ComponentsBrafab/FavouritesMenu'),
        });
        renderReact(
            <Provider store={store}>
                <FavouritesMenu isMobile={false} />
            </Provider>,
            favouritesEl
        );
    }

    const cookieBarEl = document.getElementById('js-cookieBar');
    if (cookieBarEl) {
        const CookieBar = DynamicComponent({
            loader: () => import('./ComponentsBrafab/CookieBar'),
        });
        import('./ReducersBrafab/CookieBar.reducer').then(({ cookieBar }) => {
            store.injectReducer('cookieBar', cookieBar);
            renderReact(
                <Provider store={store}>
                    <CookieBar {...{ cookiebarVisibleValue: true }} />
                </Provider>,
                cookieBarEl
            );
        });
    }

    var languagePickerPopUp = document.getElementById('js-languagePickerPopUp');
    if (languagePickerPopUp) {
        const LanguagePickerPopUp = DynamicComponent({
            loader: () => import('./ComponentsBrafab/LanguagePickerPopUp'),
        });
        ReactDOM.render(
            <Provider store={store}>
                <LanguagePickerPopUp />
            </Provider>,
            languagePickerPopUp
        );
    }

    var searchResult = document.getElementById('js-searchResult');
    if (searchResult) {
        const SearchResult = DynamicComponent({
            loader: () => import('./ComponentsBrafab/SearchResult'),
        });
        ReactDOM.render(
            <Provider store={store}>
                <SearchResult />
            </Provider>,
            searchResult
        );
    }

    var facetedSearch = document.getElementById('facetedSearch');
    if (facetedSearch) {
        renderReact(
            <Provider store={store}>
                <FacetedSearch />
            </Provider>,
            facetedSearch
        );
    }
    var facetedSearchCompact = document.getElementById('facetedSearchCompact');
    if (facetedSearchCompact) {
        const showCompactForDesktop = facetedSearchCompact.dataset.showDesktop;
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer showCompactForDesktop={showCompactForDesktop} />
            </Provider>,
            facetedSearchCompact
        );
    }

    var lazyLoading = document.getElementById('lazyloader');
    if (lazyLoading) {
        import('./brafab.lazypagination');
    }

    var lightBoxImagesEl = document.getElementById('lightBoxImages');
    if (lightBoxImagesEl && !lightBoxImagesInitiated) {
        lightBoxImagesInitiated = true;
        const LightboxImages = DynamicComponent({
            loader: () => import('./ComponentsBrafab/LightboxImages'),
        });
        import('./Reducers/LightboxImages.reducer').then(({ lightboxImages }) => {
            store.injectReducer('lightboxImages', lightboxImages);

            const images = Array.from(lightBoxImagesEl.querySelectorAll('template')).map((img) => {
                return {
                    html: img.innerHTML,
                    src: img.dataset.src,
                    isExploded: img.dataset.sparepart,
                    videoLink: img.dataset.link,
                };
            });

            const sparePartIconArray = Array.from(
                lightBoxImagesEl.querySelectorAll('.spare-part-icon')
            );
            const sparePartIcon = sparePartIconArray.map((img) => {
                return {
                    html: img.innerHTML,
                    src: img.dataset.src,
                };
            });

            renderReact(
                <Provider store={store}>
                    <LightboxImages images={images} sparepartIcon={sparePartIcon} />
                </Provider>,
                lightBoxImagesEl
            );
        });
    }

    var sliders = document.querySelectorAll('.slider');
    if (sliders.length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./ComponentsBrafab/Slider'),
        });
        let sliderContainsBuyButtons = false;
        Array.from(sliders).forEach((slider, index) => {
            const values = [...slider.querySelectorAll('template')].map((block) => {
                if (block.innerHTML.indexOf('buy-button') > -1) {
                    sliderContainsBuyButtons = true;
                }
                return {
                    html: block.innerHTML,
                };
            });
            if (values.length > 0) {
                renderReact(<Slider values={values} bootstrapComponents={sliderContainsBuyButtons} />, slider);
            }
        });
    }

    const buyButtons = document.querySelectorAll('buy-button');
    if (buyButtons.length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./ComponentsBrafab/BuyButton'),
        });
        Array.from(buyButtons).forEach((button) => {
            const {
                articleNumber,
                quantityFieldId,
                href,
                cssClass,
                label,
                multiple,
            } = button.dataset;
            renderReact(
                <Provider store={store}>
                    <BuyButton
                        {...{
                            label,
                            articleNumber,
                            multiple,
                            quantityFieldId,
                            href,
                            cssClass,
                        }}
                    />
                </Provider>,
                button
            );
        });
    }

    const findResellerButton = document.getElementById("reseller-map-link");
    if (findResellerButton) {
        findResellerButton.addEventListener('click',
            function (e) {
                e.preventDefault();
                fetch('/api/visit/findreseller', {
                    method: "POST",
                    body: JSON.stringify({
                        articleId: this.getAttribute('data')
                    }),
                    headers: {
                        "Content-type": "application/json; charset=UTF-8",
                        'litium-request-context': JSON.stringify(
                            window.__litium.requestContext
                        )
                    }
                })
                    .then((response) => response.json())
                    .then((data) => {
                        if (data.dataLayer && typeof dataLayer !== 'undefined' && dataLayer !== null) {
                            gtag('event', data.event, data.dataLayer);
                        }
                        window.location.href = findResellerButton.href;
                    })
                    .catch(() => {
                        window.location.href = findResellerButton.href;
                    })
            }
        );
    }

    const favouriteButtons = document.querySelectorAll('favourite-button');
    if (favouriteButtons.length > 0) {
        const FavouriteButton = DynamicComponent({
            loader: () => import('./ComponentsBrafab/FavouriteButton'),
        });
        Array.from(favouriteButtons).forEach((button) => {
            const {
                articleNumber,
                favouriteClass,
                cssClass,
                amountOfSame,
                articleName,
            } = button.dataset;
            renderReact(
                <Provider store={store}>
                    <FavouriteButton
                        {...{ articleNumber, favouriteClass, cssClass, amountOfSame, articleName }}
                    />
                </Provider>,
                button
            );
        });
    }

    var checkoutb2b = document.getElementById('checkoutb2b');
    if (checkoutb2b) {
        const Checkout = DynamicComponent({
            loader: () => import('./ComponentsBrafab/CheckoutB2B/CheckoutB2B'),
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            checkoutb2b
        );
    }
    var checkoutb2c = document.getElementById('checkoutb2c');
    if (checkoutb2c) {
        const Checkout = DynamicComponent({
            loader: () => import('./ComponentsBrafab/CheckoutB2C/CheckoutB2C'),
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            checkoutb2c
        );
    }

    var myPagePersons = document.getElementById('myPagePersons');
    if (myPagePersons) {
        const PersonList = DynamicComponent({
            loader: () => import('./ComponentsBrafab/PersonListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <PersonList />
            </Provider>,
            myPagePersons
        );
    }
    var myPageAddresses = document.getElementById('myPageAddresses');
    if (myPageAddresses) {
        const AddressList = DynamicComponent({
            loader: () => import('./ComponentsBrafab/AddressListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <AddressList />
            </Provider>,
            myPageAddresses
        );
    }

    var myPageActiveConsumerOrders = document.getElementById('myPageActiveConsumerOrders');
    if (myPageActiveConsumerOrders) {
        const ActiveConsumerOrderList = DynamicComponent({
            loader: () => import('./ComponentsBrafab/ActiveConsumerOrderListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <ActiveConsumerOrderList />
            </Provider>,
            myPageActiveConsumerOrders
        );
    }
    var myPageConsumerOrderHistory = document.getElementById('myPageConsumerOrderHistory');
    if (myPageConsumerOrderHistory) {
        const ConsumerOrderHistoryList = DynamicComponent({
            loader: () => import('./ComponentsBrafab/ConsumerOrderHistoryListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <ConsumerOrderHistoryList />
            </Provider>,
            myPageConsumerOrderHistory
        );
    }

    var orderHistoryPage = document.getElementById('orderHistoryPage');
    if (orderHistoryPage) {
        const OrderList = DynamicComponent({
            loader: () => import('./ContainersBrafab/OrderHistoryListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <OrderList />
            </Provider>,
            orderHistoryPage
        );
    }

    var creitOrderHistoryPage = document.getElementById('creditOrderHistoryPage');
    if (creitOrderHistoryPage) {
        const CreditOrderList = DynamicComponent({
            loader: () => import('./ContainersBrafab/CreditOrderHistoryListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <CreditOrderList />
            </Provider>,
            creitOrderHistoryPage
        );
    }

    var backOrderPage = document.getElementById('js-myPageBackOrder');
    if (backOrderPage) {
        const BackOrderRowList = DynamicComponent({
            loader: () => import('./ComponentsBrafab/BackOrderRowList'),
        });
        renderReact(
            <Provider store={store}>
                <BackOrderRowList />
            </Provider>,
            backOrderPage
        );
    }

    var invoiceHistoryPage = document.getElementById('js-invoiceHistoryPage');
    if (invoiceHistoryPage) {
        const InvoiceHistory = DynamicComponent({
            loader: () => import('./ContainersBrafab/InvoiceHistoryContainer'),
        });
        renderReact(
            <Provider store={store}>
                <InvoiceHistory />
            </Provider>,
            invoiceHistoryPage
        );
    }

    var shipmentHistoryPage = document.getElementById('js-shipmentHistoryPage');
    if (shipmentHistoryPage) {
        const ShipmentHistory = DynamicComponent({
            loader: () => import('./ContainersBrafab/ShipmentHistoryContainer'),
        });
        renderReact(
            <Provider store={store}>
                <ShipmentHistory />
            </Provider>,
            shipmentHistoryPage
        );
    }

    var returns = document.getElementById('js-returns');
    if (returns) {
        const ReturnListContainer = DynamicComponent({
            loader: () => import('./ContainersBrafab/ReturnListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <ReturnListContainer returnMode={'active'} />
            </Provider>,
            returns
        );
    }

    var returnsCreationPage = document.getElementById('js-returnsCreationPage');
    if (returnsCreationPage) {
        const PendingReturnContainer = DynamicComponent({
            loader: () => import('./ContainersBrafab/ReturnsContainer')
        });
        renderReact(
            <Provider store={store}>
                <PendingReturnContainer />
            </Provider>,
            returnsCreationPage
        );
    }

    var closedReturns = document.getElementById('js-closed-returns');
    if (closedReturns) {
        const ReturnListContainer = DynamicComponent({
            loader: () => import('./ContainersBrafab/ReturnListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <ReturnListContainer returnMode={'closed'} />
            </Provider>,
            closedReturns
        );
    }

    var languagePickerFooter = document.getElementById('js-languagePickerFooter');
    if (languagePickerFooter) {
        renderReact(
            <Provider store={store}>
                <LanguagePickerFooter />
            </Provider>,
            languagePickerFooter
        );
    }
    var selectOrganizationContainer = document.getElementById('js-selectOrganizationContainer');
    if (selectOrganizationContainer) {
        import('./ReducersBrafab/SelectOrganization.reducer').then(({ selectOrganization }) => {
            store.injectReducer('selectOrganization', selectOrganization);
            const SelectOrganizationContainer = DynamicComponent({
                loader: () => import('./ContainersBrafab/SelectOrganizationContainer'),
            });
            const defaultName = selectOrganizationContainer.dataset.orgName ?? '';
            renderReact(
                <Provider store={store}>
                    <SelectOrganizationContainer defaultName={defaultName} />
                </Provider>,
                selectOrganizationContainer
            );
        });
    }

    var offerPage = document.getElementById('offerPage');
    if (offerPage) {
        import('./ReducersBrafab/GenericExport.reducer').then(({ genericExport }) => {
            store.injectReducer('genericExport', genericExport);
        });
        const OfferPage = DynamicComponent({
            loader: () => import('./ComponentsBrafab/OfferPage'),
        });
        renderReact(
            <Provider store={store}>
                <OfferPage />
            </Provider>,
            offerPage
        );
    }

    const klevuProductRecommendation = document.querySelectorAll('.js-klevu-recs');
    if (klevuProductRecommendation != null && klevuProductRecommendation.length > 0) {
        klevuProductRecommendation.forEach((rec) => {
            rec.classList.remove('js-klevu-recs');
        });
        import('./brafab.klevurecommentation').then(({ handleRecommendation }) => {
            klevuProductRecommendation.forEach((rec) => {
                handleRecommendation(rec);
            });
        });
    }

    const catnav = document.querySelectorAll('[data-klevu]');
    if (catnav != null && catnav.length > 0) {
        import('./brafab.klevu').then(({ handleAnalaytics }) => {
            catnav.forEach((box, index) => {
                handleAnalaytics(box, index);
            });
        });
    }

    let data = sessionStorage.getItem('klevu');
    if (data) {
        sessionStorage.removeItem('klevu');
        fetch(data, {
            method: 'GET',
            headers: {
                pragma: 'no-cache',
                'cache-control': 'no-cache',
                'litium-request-context': JSON.stringify(window.__litium.requestContext),
            },
        });
    }

    const tippy = document.querySelectorAll('.tooltip');
    if (tippy != null && tippy.length > 0) {
        import('tippy.js').then(({ default: tippy }) => {
            tippy('.tooltip', {
                content: '',
                allowHTML: true,
                trigger: 'click',
            });
        });
    }

    const cookieButton = document.getElementById('js-cookieButton');
    if (cookieButton) {
        const CookieSettings = DynamicComponent({
            loader: () => import('./ComponentsBrafab/CookieBar'),
        });
        import('./ReducersBrafab/CookieBar.reducer').then(({ cookieBar }) => {
            store.injectReducer('cookieBar', cookieBar);
            const { functional, analytics, marketing, personalization } = cookieButton.dataset;
            renderReact(
                <Provider store={store}>
                    <CookieSettings
                        {...{
                            cookiebarVisibleValue: false,
                            showButton: true,
                            functionalValue: functional === 'true',
                            analyticsValue: analytics === 'true',
                            marketingValue: marketing === 'true',
                            personalizationValue: personalization === 'true',
                        }}
                    />
                </Provider>,
                cookieButton
            );
        });
    }
};
function initProductDetailVariantSelector() {
    const isMobile = () => window.matchMedia('(max-width: 767px)').matches;
    const toggles = document.querySelectorAll('.dropdown-toggle');
    const containers = document.querySelectorAll('.product-detail__dropdown');

    function lockBodyScroll() {
        document.body.dataset.scrollY = window.scrollY;
        document.body.style.top = `-${window.scrollY}px`;
        document.body.classList.add('body-lock-scroll');
    }
    function unlockBodyScroll() {
        if (document.body.classList.contains('body-lock-scroll')) {
            document.body.classList.remove('body-lock-scroll');
            window.scrollTo(0, parseInt(document.body.dataset.scrollY || '0'));
            document.body.style.top = '';
            delete document.body.dataset.scrollY;
        }
    }
    function closeAllDropdowns(exceptId) {
        toggles.forEach(toggle => {
            if (!exceptId || toggle.id !== exceptId) {
                toggle.checked = false;
            }
        });
        containers.forEach(container => {
            container.classList.remove('mobile-active');
        });
        if (window.matchMedia('(max-width: 767px)').matches) {
            unlockBodyScroll();
        }
    }

    containers.forEach(container => {
        const label = container.querySelector('label');
        const toggle = container.querySelector('.dropdown-toggle');
        const overlay = container.querySelector('.product-detail__dropdown-mobile-overlay');
        const popup = container.querySelector('.product-detail__dropdown-mobile-popup');

        if (label && toggle) {
            label.addEventListener('click', function (e) {
                setTimeout(() => {
                    if (toggle.checked) {
                        closeAllDropdowns(toggle.id);
                        if (isMobile()) {
                            container.classList.add('mobile-active');
                            lockBodyScroll();
                        }
                    } else {
                        container.classList.remove('mobile-active');
                        unlockBodyScroll();
                    }
                }, 0);
            });
        }

        if (overlay) {
            overlay.addEventListener('click', function () {
                closeAllDropdowns();
            });
        }

        if (popup) {
            popup.addEventListener('click', function (e) {
                if (e.target.tagName === 'A') {
                    closeAllDropdowns();
                }
            });
        }
    });

    document.addEventListener('click', function (event) {
        let isDropdown = false;
        containers.forEach(container => {
            if (container.contains(event.target)) {
                isDropdown = true;
            }
        });
        if (!isDropdown && !isMobile()) {
            closeAllDropdowns();
        }
    });

    document.addEventListener('click', function (e) {
        if (e.target.matches('.product-detail__dropdown-mobile-title--close')) {
            closeAllDropdowns();
        }
    });

    window.addEventListener('resize', function () {
        if (!isMobile()) {
            containers.forEach(container => {
                container.classList.remove('mobile-active');
            });
            unlockBodyScroll();
        }
    });
}

bootstrapComponents();
initProductDetailVariantSelector();

var lazyLoading = document.getElementById('lazyloader');
if (lazyLoading) {
    import('./brafab.lazypagination');
}

const splideEl = document.querySelectorAll('.splide');
if (splideEl.length > 0) {
    import('./brafab.splide');
}

const footerHeaders = document.querySelectorAll('.footer__header');
if (footerHeaders) {
    for (let i = 0; i < footerHeaders.length; i++) {
        const footerHeader = footerHeaders[i];
        footerHeader.addEventListener('click', function () {
            footerHeader.classList.toggle('active');
        });
    }
}

if (window.__litium.constants.isC10) {
    pageTimer = setTimeout(() => {
        fetch('/api/visit/c10', {
            headers: {
                'litium-request-context': JSON.stringify(window.__litium.requestContext),
            },
        })
            .then((response) => response.json())
            .then((data) => {
                gtag('event', 'viewcontent10', { eventId: data.eventId });
                clearTimeout(pageTimer);
            });
    }, 10000);
}
